import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const tvTypes = [
  { icon: 'ri-tv-line', label: 'CRT Televisions', desc: 'Old tube TVs with lead & toxic glass — must be recycled properly' },
  { icon: 'ri-tv-line', label: 'Flat Screen TVs', desc: 'LED, LCD, and flat panel televisions of all sizes' },
  { icon: 'ri-sun-line', label: 'Plasma TVs', desc: 'Plasma display panels from all major brands' },
  { icon: 'ri-computer-line', label: 'OLED & QLED TVs', desc: 'Latest generation OLED, QLED, and 4K/8K displays' },
  { icon: 'ri-computer-line', label: 'Computer Monitors', desc: 'Desktop monitors, CRT monitors, LCD, and LED screens' },
  { icon: 'ri-film-line', label: 'Projectors', desc: 'Home theater and commercial projectors' },
];

const whyRecycle = [
  { icon: 'ri-drop-line', title: 'Lead & Mercury', desc: 'A single CRT TV contains up to 8 lbs of lead. Flat screens have mercury in backlight tubes. Both are serious environmental toxins.' },
  { icon: 'ri-earth-line', title: 'California Law', desc: "California's Electronic Waste Recycling Act (SB 20/SB 50) requires proper TV recycling. Illegal dumping carries heavy fines." },
  { icon: 'ri-recycle-line', title: 'Valuable Materials', desc: 'TVs contain copper, aluminum, glass, and rare earth metals that can be recovered and reused to make new products.' },
  { icon: 'ri-community-line', title: 'Community Impact', desc: 'We refurbish working TVs and donate them to underserved LA communities. Your recycled TV could brighten a family\'s home.' },
];

const faqs = [
  { q: 'Is TV recycling free in Los Angeles?', a: 'Yes — TV recycling is free at both our Los Angeles drop-off locations: 2542 W. 3rd Street (90057) and 4902 W. Slauson Ave (90056). Free TV pickup is also available for qualifying businesses with multiple TVs.' },
  { q: 'What types of TVs do you accept?', a: 'We accept all types of televisions: CRT (tube) TVs, flat screen TVs, LCD, LED, plasma, OLED, QLED, 4K, 8K, and any size from small bedside TVs to large commercial displays.' },
  { q: 'Why can\'t I just throw my TV in the trash?', a: "It's illegal in California and dangerous. TVs contain lead, mercury, cadmium, and other hazardous materials that leach into soil and groundwater. California's E-Waste law requires proper recycling." },
  { q: 'Do you pick up TVs from homes?', a: 'We offer residential TV pickup in certain areas of Los Angeles. Contact us at (213) 600-1000 to check availability and pricing for your neighborhood.' },
  { q: 'Are there any TVs you cannot accept?', a: "We accept virtually all consumer and commercial TVs. Televisions with shattered screens or extreme damage may be subject to a handling fee. Call us first if you're unsure." },
  { q: 'Can I drop off multiple TVs at once?', a: "Absolutely. There's no limit on how many TVs you can drop off at our facilities. For very large quantities (10+), call ahead so we can prepare space." },
];

const TVRecyclingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

  return (
    <>
      <title>TV Recycling Los Angeles | Free CRT &amp; Flat Screen TV Disposal | Greenly</title>
      <meta name="description" content="Free TV recycling in Los Angeles — CRT, flat screen, LCD, plasma & OLED accepted. No appointment needed. 2 drop-off locations. CalRecycle certified. Call (213) 600-1000." />
      <meta name="keywords" content="TV recycling Los Angeles, CRT TV recycling LA, flat screen TV disposal Los Angeles, free TV recycling Los Angeles, television recycling LA" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/tv-recycling`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${SITE_URL}/tv-recycling#webpage`,
            "url": `${SITE_URL}/tv-recycling`,
            "name": "TV Recycling Los Angeles | Free CRT & Flat Screen TV Disposal | Greenly",
            "description": "Free TV recycling in Los Angeles. We accept CRT TVs, flat screen TVs, LCD, plasma, OLED & all televisions. 2 drop-off locations. CalRecycle certified. Call (213) 600-1000.",
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
                { "@type": "ListItem", "position": 2, "name": "TV Recycling Los Angeles", "item": `${SITE_URL}/tv-recycling` }
              ]
            }
          },
          {
            "@type": "Service",
            "@id": `${SITE_URL}/tv-recycling#service`,
            "name": "TV Recycling Los Angeles",
            "description": "Free TV recycling in Los Angeles. We accept CRT TVs, flat screen TVs, LCD, plasma, OLED and all television types at two drop-off locations. CalRecycle certified CEWD-127531.",
            "url": `${SITE_URL}/tv-recycling`,
            "provider": { "@id": `${SITE_URL}/#business` },
            "areaServed": { "@type": "City", "name": "Los Angeles" },
            "serviceType": "Electronics Recycling",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "description": "Free TV recycling drop-off" }
          },
          {
            "@type": "FAQPage",
            "@id": `${SITE_URL}/tv-recycling#faq`,
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          }
        ]
      })}</script>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative bg-gray-900 pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Stack%20of%20old%20televisions%20CRT%20tube%20TVs%20and%20flat%20screen%20TVs%20sorted%20and%20organized%20for%20recycling%20in%20a%20clean%20electronics%20recycling%20facility%2C%20bright%20warehouse%20lighting%2C%20green%20eco-friendly%20recycling%20center%2C%20various%20sizes%20of%20televisions%20ready%20for%20responsible%20disposal%2C%20professional%20industrial%20setting&width=1400&height=600&seq=tvhero001&orientation=landscape"
              alt="TV recycling Los Angeles — CRT and flat screen televisions ready for recycling"
              className="w-full h-full object-cover object-center opacity-35"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/85 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'TV Recycling Los Angeles' }]} />
            <div className="max-w-2xl mt-8">
              <div className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-tv-line"></i>
                Free TV Recycling
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">
                TV Recycling
                <span className="block text-[#74C69D]">in Los Angeles</span>
              </h1>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                Los Angeles' certified TV recycling service — we accept <strong className="text-white">CRT TVs, flat screen TVs, plasma, LCD, OLED</strong> and all television types. Two convenient drop-off locations. Free business pickup available.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <a href="tel:+12136001000" className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-phone-fill"></i>(213) 600-1000
                </a>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-map-pin-line"></i>Get Directions
                </Link>
              </div>
              <div className="flex flex-wrap gap-5">
                {['CRT TVs Accepted', 'Flat Screens', 'No Appointment Needed', 'CalRecycle Certified'].map((t) => (
                  <div key={t} className="flex items-center gap-1.5 text-green-300 text-xs font-semibold">
                    <i className="ri-check-line text-[#74C69D]"></i>{t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="bg-[#1B6B3A] py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 text-white text-xs md:text-sm">
              {[
                { icon: 'ri-map-pin-line', text: '2 LA Drop-Off Locations' },
                { icon: 'ri-tv-line', text: 'All TV Types Accepted' },
                { icon: 'ri-award-line', text: 'CalRecycle CEWD-127531' },
                { icon: 'ri-gift-line', text: 'Free for Residential Drop-Off' },
              ].map((i) => (
                <div key={i.text} className="flex items-center gap-2">
                  <i className={`${i.icon} text-[#74C69D]`}></i>
                  <span className="font-semibold">{i.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why recycle TVs */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
              <div className="w-full lg:w-1/2">
                <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Why It Matters</div>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5 leading-tight">
                  Why Proper TV Recycling in <strong>Los Angeles</strong> Matters
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Televisions are one of the most hazardous types of household electronics waste. A single CRT (tube) TV can contain up to <strong className="text-gray-900">8 pounds of lead</strong>, while LCD flat screens contain mercury in their backlight tubes. When illegally dumped, these toxins contaminate Los Angeles soil and groundwater for decades.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  California's <strong className="text-gray-900">Electronic Waste Recycling Act (SB 20/SB 50)</strong> makes it illegal to dispose of televisions in regular trash or at landfills. Greenly Electronics Recycling is your CalRecycle-certified partner for safe, responsible TV recycling in Los Angeles.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  We disassemble every TV responsibly — extracting and neutralizing hazardous materials, and recovering valuable copper, aluminum, and glass for reuse in new products.
                </p>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white font-bold px-5 py-2.5 rounded-md hover:bg-[#2D8653] transition-colors cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-tv-line"></i>Recycle Your TV Now
                </Link>
              </div>
              <div className="w-full lg:w-1/2 grid grid-cols-2 gap-4">
                {whyRecycle.map((w) => (
                  <div key={w.title} className="bg-[#F4FAF7] rounded-xl p-5">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#1B6B3A] rounded-lg mb-3">
                      <i className={`${w.icon} text-white text-sm`}></i>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{w.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{w.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* TV types accepted */}
        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">What We Accept</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                All Television Types Accepted for <strong>Recycling in LA</strong>
              </h2>
              <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">
                From vintage tube TVs to the latest 8K displays — if it shows a picture, we'll recycle it responsibly.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {tvTypes.map((t) => (
                <div key={t.label} className="bg-white rounded-xl p-6 flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className={`${t.icon} text-[#1B6B3A] text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{t.label}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Drop-off locations */}
        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Drop-Off Locations</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                2 Convenient TV Recycling Drop-Off Locations in Los Angeles
              </h2>
              <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">No appointment needed. Just drive up and we'll unload your TVs for you.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className="ri-map-pin-2-fill text-[#1B6B3A] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Location 1 — West 3rd Street</div>
                    <div className="text-gray-600 text-sm mt-1">2542 W. 3rd Street, Los Angeles, CA 90057</div>
                    <div className="text-gray-400 text-xs mt-1">Mon–Fri 8am–5pm · Sat 9am–2pm</div>
                  </div>
                </div>
                <a
                  href="https://maps.app.goo.gl/1GFb4ZnoPCREHhzy6"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 text-[#1B6B3A] font-semibold text-sm hover:underline cursor-pointer"
                >
                  <i className="ri-direction-line"></i>Get Directions
                </a>
              </div>
              <div className="border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className="ri-map-pin-2-fill text-[#1B6B3A] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Location 2 — West Slauson Ave</div>
                    <div className="text-gray-600 text-sm mt-1">4902 W. Slauson Ave, Los Angeles, CA 90056</div>
                    <div className="text-gray-400 text-xs mt-1">Mon–Fri 8am–5pm · Sat 9am–2pm</div>
                  </div>
                </div>
                <a
                  href="https://maps.google.com/?q=4902+W+Slauson+Ave+Los+Angeles+CA+90056"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 text-[#1B6B3A] font-semibold text-sm hover:underline cursor-pointer"
                >
                  <i className="ri-direction-line"></i>Get Directions
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-10 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-7">
              <h2 className="text-xl font-extrabold text-gray-900">Also Recycle These Electronics at the Same Drop-Off</h2>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { label: 'Computer Recycling', path: '/computer-recycling', icon: 'ri-computer-line' },
                { label: 'Battery Recycling', path: '/battery-recycling', icon: 'ri-battery-line' },
                { label: 'Hard Drive Destruction', path: '/hard-drive-destruction', icon: 'ri-hard-drive-line' },
                { label: 'All Electronics', path: '/services', icon: 'ri-recycle-line' },
              ].map((s) => (
                <Link key={s.path} to={s.path} className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A] px-5 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                  <i className={s.icon}></i>{s.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Common Questions</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">TV Recycling FAQs — Los Angeles</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span className="text-sm font-bold text-gray-900">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`text-[#1B6B3A] transition-transform duration-200 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-[#1B6B3A]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">Ready to Recycle Your TV in Los Angeles?</h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Drop off your TVs at either of our Los Angeles locations — no appointment needed. Or call us to arrange a business pickup.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:+12136001000" className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-phone-fill"></i>Call (213) 600-1000
              </a>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-map-pin-line"></i>Get Directions
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default TVRecyclingPage;
